import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tabs,
  Tab,
  Box,
} from "@mui/material";
import {
  loginCatalogueVisitor,
  registerCatalogueVisitor,
} from "./catalogueVisitorClient";
import type { CatalogueVisitorProfile } from "./catalogueVisitorSession";
import { getLastVisitedCatalogue } from "./catalogueVisitorSession";

function isValidOptionalEmail(email: string): boolean {
  const e = email.trim();
  if (!e) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

type CatalogueVisitorAuthModalProps = {
  open: boolean;
  onClose: () => void;
  onSignedIn: (token: string, profile: CatalogueVisitorProfile | null) => void;
  /** From route; merged with last visited catalogue when registering from visitor account. */
  catalogueId?: string | null;
};

const CatalogueVisitorAuthModal: React.FC<CatalogueVisitorAuthModalProps> = ({
  open,
  onClose,
  onSignedIn,
  catalogueId: catalogueIdProp,
}) => {
  const [tab, setTab] = useState(0);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setError(null);
  };

  const handleClose = () => {
    if (!busy) onClose();
  };

  const resolveCatalogueIdForRegister = (): string => {
    const fromRoute = catalogueIdProp != null ? String(catalogueIdProp).trim() : "";
    if (fromRoute) return fromRoute;
    const last = getLastVisitedCatalogue();
    return last?.catalogueId?.trim() || "";
  };

  const parseAuthData = (data: unknown): { token: string; profile: CatalogueVisitorProfile | null } | null => {
    if (!data || typeof data !== "object") return null;
    const d = data as Record<string, unknown>;
    const token = d.token != null ? String(d.token) : "";
    if (!token) return null;
    const rawProfile = d.profile;
    if (rawProfile && typeof rawProfile === "object") {
      const p = rawProfile as Record<string, unknown>;
      const visitorId = p.visitorId != null ? String(p.visitorId) : "";
      if (visitorId) {
        return {
          token,
          profile: {
            visitorId,
            name: p.name != null ? String(p.name) : "",
            phone: p.phone != null ? String(p.phone) : "",
            email: p.email != null ? String(p.email) : undefined,
          },
        };
      }
    }
    return { token, profile: null };
  };

  const onRegister = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!isValidOptionalEmail(email)) {
        setError("Enter a valid email or leave it blank.");
        return;
      }

      const cid = resolveCatalogueIdForRegister();
      const payload: Parameters<typeof registerCatalogueVisitor>[0] = {
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
      };
      if (cid) {
        payload.catalogueId = cid;
      }
      const orgTrim = organizationName.trim();
      if (orgTrim) {
        payload.organizationName = orgTrim;
      }

      const json = await registerCatalogueVisitor(payload);
      if (json?.success && json.data) {
        const parsed = parseAuthData(json.data);
        if (parsed) {
          onSignedIn(parsed.token, parsed.profile);
          onClose();
          return;
        }
      }
      setError(json?.message || "Could not create your account.");
    } catch {
      setError("Network error. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const onLogin = async () => {
    setBusy(true);
    setError(null);
    try {
      const json = await loginCatalogueVisitor({ phone: phone.trim() });
      if (json?.success && json.data) {
        const parsed = parseAuthData(json.data);
        if (parsed) {
          onSignedIn(parsed.token, parsed.profile);
          onClose();
          return;
        }
      }
      setError(json?.message || "Could not sign in.");
    } catch {
      setError("Network error. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const hkpSans = '"Hanken Grotesk", -apple-system, system-ui, sans-serif';
  const btnPrimarySx = {
    fontFamily: hkpSans,
    fontWeight: 650,
    letterSpacing: "-0.01em",
    textTransform: "none" as const,
    borderRadius: "100px",
    px: 3,
    py: 1.25,
    bgcolor: "#0071E3",
    boxShadow: "0 10px 24px -10px rgba(0, 113, 227, 0.55)",
    "&:hover": { bgcolor: "#0058C2" },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          fontFamily: hkpSans,
          color: "#1D1D1F",
          borderRadius: "18px",
        },
      }}
    >
      <DialogTitle sx={{ fontFamily: hkpSans, fontWeight: 650, letterSpacing: "-0.02em" }}>
        Your details for this studio
      </DialogTitle>
      <DialogContent>
        <Tabs
          value={tab}
          onChange={(_, v) => {
            setTab(v);
            resetForm();
          }}
          sx={{
            mb: 2,
            minHeight: 40,
            "& .MuiTab-root": { fontFamily: hkpSans, fontWeight: 650, color: "#51515A" },
            "& .MuiTab-root.Mui-selected": { color: "#0071E3" },
            "& .MuiTabs-indicator": { backgroundColor: "#0071E3" },
          }}
        >
          <Tab label="Create account" sx={{ textTransform: "none", fontWeight: 600 }} />
          <Tab label="Sign in" sx={{ textTransform: "none", fontWeight: 600 }} />
        </Tabs>
        <p className="hkp-visitor-trust-note" style={{ marginTop: 0, marginBottom: "1rem", fontSize: "0.88rem", color: "#51515A" }}>
          We’ll use this so the studio can follow up. GST and address can wait until you submit your selection.
        </p>
        {tab === 0 ? (
          <Box display="flex" flexDirection="column" gap={1.5}>
            <TextField
              label="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              size="small"
            />
            <TextField
              label="Business or organization name (optional)"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              fullWidth
              size="small"
              placeholder="Leave blank to use your name as the party label"
            />
            <TextField
              label="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              fullWidth
              required
              size="small"
              placeholder="10-digit mobile"
            />
            <TextField
              label="Email (optional)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              size="small"
              type="email"
            />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={1.5}>
            <TextField
              label="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              fullWidth
              required
              size="small"
              placeholder="Same number you registered with"
            />
          </Box>
        )}
        {error ? (
          <p style={{ color: "#b42318", fontSize: "0.85rem", marginTop: "0.75rem" }} role="alert">
            {error}
          </p>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={busy}
          sx={{
            fontFamily: hkpSans,
            fontWeight: 650,
            textTransform: "none",
            color: "#0071E3",
            borderRadius: "100px",
          }}
        >
          Cancel
        </Button>
        {tab === 0 ? (
          <Button
            variant="contained"
            onClick={onRegister}
            disabled={busy || !name.trim() || !phone.trim()}
            sx={btnPrimarySx}
          >
            {busy ? "Saving…" : "Continue"}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={onLogin}
            disabled={busy || !phone.trim()}
            sx={btnPrimarySx}
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CatalogueVisitorAuthModal;
